import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { RaptorLogo } from '@/components/RaptorLogo';
import { SurveyForm, SchemaShape, isVisible as isQuestionVisible } from '@/components/SurveyForm';
import { toast } from 'sonner';
import { CheckCircle2, MapPin, ChevronRight, AlertTriangle, Loader2 } from 'lucide-react';

interface FieldSpec { key: string; label: string; required: boolean }
interface Resolved { client_name: string; template_name: string; site_fields: FieldSpec[]; schema: SchemaShape }

export const SiteIntakePage: React.FC = () => {
  const { token } = useParams();
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [step, setStep] = useState<'details' | 'survey' | 'done' | 'error'>('details');
  const [errMsg, setErrMsg] = useState('');
  const [site, setSite] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/public/site-intake/${token}`);
        setResolved(r.data);
      } catch (e) {
        setErrMsg((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          || 'This link is no longer active. Please ask for a new one.');
        setStep('error');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const missingSite = useMemo(() => (resolved?.site_fields || [])
    .filter((f) => f.required && !String(site[f.key] || '').trim()).map((f) => f.label), [resolved, site]);

  const submit = async (): Promise<void> => {
    if (!resolved) return;
    // enforce required survey questions that are visible
    const reqMissing: string[] = [];
    resolved.schema.sections.forEach((s) => s.questions.forEach((q) => {
      if (q.required && q.type !== 'signature_ref' && q.type !== 'photo_ref'
        && isQuestionVisible(q, answers) && !answers[q.question_code]) reqMissing.push(q.text);
    }));
    if (reqMissing.length) { toast.error(`Please answer: ${reqMissing.slice(0, 3).join(', ')}${reqMissing.length > 3 ? '…' : ''}`); return; }
    setBusy(true);
    try {
      const payload = {
        site,
        answers: Object.entries(answers).map(([question_code, value]) => ({ question_code, value })),
      };
      await axios.post(`${API_BASE}/public/site-intake/${token}/submit`, payload);
      setStep('done');
    } catch (e) {
      toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-raptor-navy/60"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-raptor-neutral/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6"><RaptorLogo /></div>

        {step === 'error' && (
          <Card data-testid="intake-error"><CardContent className="py-12 text-center">
            <AlertTriangle className="mx-auto mb-3 text-amber-500" size={32} />
            <div className="font-headline text-lg text-raptor-navy">Link unavailable</div>
            <p className="text-sm text-raptor-navy/60 mt-1">{errMsg}</p>
          </CardContent></Card>
        )}

        {step === 'done' && (
          <Card data-testid="intake-done"><CardContent className="py-12 text-center">
            <CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={36} />
            <div className="font-headline text-xl text-raptor-navy">Thank you — submitted</div>
            <p className="text-sm text-raptor-navy/60 mt-1">The site details have been sent for review. You can close this page, or submit another site using the same link.</p>
            <Button variant="outline" className="mt-4" onClick={() => { setSite({}); setAnswers({}); setStep('details'); }} data-testid="intake-another">Submit another site</Button>
          </CardContent></Card>
        )}

        {resolved && (step === 'details' || step === 'survey') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MapPin size={18} className="text-raptor-blue" /> Site details — {resolved.client_name}</CardTitle>
              <CardDescription>
                {step === 'details'
                  ? 'Step 1 of 2 — tell us about the premises.'
                  : `Step 2 of 2 — ${resolved.template_name}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {step === 'details' ? (
                <>
                  {resolved.site_fields.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                      <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                      <Input value={site[f.key] || ''} onChange={(e) => setSite({ ...site, [f.key]: e.target.value })}
                        data-testid={`intake-site-${f.key}`} />
                    </div>
                  ))}
                  <div className="flex justify-end pt-2">
                    <Button disabled={missingSite.length > 0} onClick={() => setStep('survey')} data-testid="intake-to-survey">
                      Continue to survey <ChevronRight size={15} />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <SurveyForm schema={resolved.schema} answers={answers}
                    onChange={(code, value) => setAnswers((a) => ({ ...a, [code]: value }))} />
                  <div className="flex justify-between pt-2">
                    <Button variant="outline" onClick={() => setStep('details')}>Back</Button>
                    <Button disabled={busy} onClick={() => void submit()} data-testid="intake-submit">
                      {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Submit site
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SiteIntakePage;
